<template>
    <div>
        <!-- Bank name -->
        <div class="row tw-mb-4">
            <div class="col-md-6">
                <label class="required form-col-label control-label">{{
                    $t("bankProfile.bankName")
                }}</label>
                <Input
                    v-model="model.bank_name"
                    :placeholder="$t('bankProfile.bankName')"
                    :class="{ 'is-invalid': validate.has('bank_name') }"
                />
                <div class="invalid-feedback" v-if="validate.has('bank_name')">
                    {{ validate.first("bank_name") }}
                </div>
            </div>
        </div>

        <!-- Accounts -->
        <div v-if="showAccounts">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-2 tw-gap-2">
                <h6 class="tw-font-semibold tw-mb-0 tw-flex-shrink-0">{{ $t("bankProfile.accounts") }}</h6>
                <div class="tw-flex tw-items-center tw-gap-2">
                    <Input
                        v-model="accountSearch"
                        search
                        clearable
                        size="small"
                        :placeholder="$t('search')"
                        style="width: 240px"
                    />
                    <Button v-if="!readonly" size="small" type="primary" ghost @click="addRow">
                        <i class="fas fa-plus tw-mr-1"></i>{{ $t("addNew") }}
                    </Button>
                </div>
            </div>

            <!-- Readonly: plain table -->
            <Table
                v-if="readonly"
                :columns="readonlyColumns"
                :data="filteredAccounts"
                size="small"
                stripe
                :no-data-text="$t('bankProfile.noAccounts')"
            >
                <template slot-scope="{ row }" slot="qr_readonly">
                    <img
                        v-if="row.qr_image"
                        :src="row.qr_image"
                        class="tw-w-8 tw-h-8 tw-object-cover tw-rounded tw-border tw-border-gray-200 tw-mx-auto"
                    />
                    <span v-else class="tw-text-gray-300">—</span>
                </template>
            </Table>

            <!-- Editable: inline inputs -->
            <Table
                v-else
                :columns="editableColumns"
                :data="filteredAccounts"
                size="small"
                stripe
                :no-data-text="$t('bankProfile.noAccounts')"
            >
                <template slot-scope="{ row }" slot="employee_id">
                    <Select
                        v-model="row.employee_id"
                        filterable
                        remote
                        clearable
                        size="small"
                        transfer
                        :remote-method="q => searchEmployee(q, row._uid)"
                        :loading="loadingEmployee[row._uid]"
                        :placeholder="$t('bankProfile.searchEmployee')"
                        @on-open-change="opened => opened && searchEmployee('', row._uid)"
                    >
                        <Option
                            v-for="emp in employeeOptions[row._uid] || []"
                            :key="emp.employee_id"
                            :value="emp.employee_id"
                            :label="emp.employee_name_en || emp.employee_name_kh"
                        >
                            <span class="tw-font-mono tw-text-gray-400 tw-text-xs tw-mr-1">{{ emp.card_id }}</span>
                            <span class="tw-text-gray-800">{{ emp.employee_name_en }}</span>
                            <span v-if="emp.employee_name_kh" class="tw-text-gray-400 tw-text-xs tw-ml-1">{{ emp.employee_name_kh }}</span>
                        </Option>
                    </Select>
                </template>

                <template slot-scope="{ row }" slot="account_no">
                    <Input
                        v-model="row.account_no"
                        size="small"
                        :placeholder="$t('bankProfile.accountNo')"
                        :class="{ 'is-invalid': validate.has(`accounts.${originalIndex(row)}.account_no`) }"
                    />
                    <div
                        class="invalid-feedback tw-text-xs"
                        v-if="validate.has(`accounts.${originalIndex(row)}.account_no`)"
                    >
                        {{ validate.first(`accounts.${originalIndex(row)}.account_no`) }}
                    </div>
                </template>

                <template slot-scope="{ row }" slot="account_name">
                    <Input
                        v-model="row.account_name"
                        size="small"
                        :placeholder="$t('bankProfile.accountName')"
                        :class="{ 'is-invalid': validate.has(`accounts.${originalIndex(row)}.account_name`) }"
                    />
                    <div
                        class="invalid-feedback tw-text-xs"
                        v-if="validate.has(`accounts.${originalIndex(row)}.account_name`)"
                    >
                        {{ validate.first(`accounts.${originalIndex(row)}.account_name`) }}
                    </div>
                </template>

                <template slot-scope="{ row }" slot="qr_image">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <img
                            v-if="row.qr_image"
                            :src="row.qr_image"
                            class="tw-w-8 tw-h-8 tw-object-cover tw-rounded tw-border tw-border-gray-200 tw-flex-shrink-0"
                        />
                        <label
                            class="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded tw-cursor-pointer tw-flex-shrink-0"
                            :class="uploadingQr[row._uid] ? 'tw-bg-gray-100' : 'tw-bg-blue-50 hover:tw-bg-blue-100'"
                            :title="$t('bankProfile.uploadQr')"
                        >
                            <i v-if="uploadingQr[row._uid]" class="fas fa-circle-notch fa-spin tw-text-gray-400" style="font-size:12px;"></i>
                            <i v-else class="fas fa-qrcode tw-text-blue-500" style="font-size:13px;"></i>
                            <input
                                type="file"
                                accept="image/*"
                                class="tw-hidden"
                                :disabled="!!uploadingQr[row._uid]"
                                @change="onQrChange($event, row)"
                            />
                        </label>
                        <a
                            v-if="row.qr_image"
                            href="#"
                            class="tw-text-gray-300 hover:tw-text-red-400"
                            @click.prevent="row.qr_image = null"
                            :title="$t('delete')"
                        >
                            <i class="fas fa-times" style="font-size:11px;"></i>
                        </a>
                    </div>
                </template>

                <template slot-scope="{ row }" slot="is_receive_cash_transfer">
                    <i-switch
                        v-model="row.is_receive_cash_transfer"
                        size="small"
                    />
                </template>

                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-danger"
                        @click.prevent="!deletingAccount[row._uid] && removeRow(row)"
                        v-tooltip="$t('delete')"
                    >
                        <i v-if="deletingAccount[row._uid]" class="fas fa-circle-notch fa-spin" style="font-size:16px;"></i>
                        <Icon v-else type="ios-trash" size="18" />
                    </a>
                </template>
            </Table>
        </div>
    </div>
</template>

<script>
import { debounce } from "lodash";
import bankAccount from "@/api/finance/bank-account";

let _uidCounter = 0;
const nextUid = () => `tmp-${++_uidCounter}-${Date.now()}`;

export default {
    name: "BankProfileFormInput",
    data() {
        return {
            uploadingQr: {},
            deletingAccount: {},
            employeeOptions: {},
            loadingEmployee: {},
            accountSearch: ""
        };
    },
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true },
        showAccounts: { type: Boolean, default: true },
        readonly: { type: Boolean, default: false }
    },
    watch: {
        'value.accounts': {
            immediate: true,
            handler(accounts) {
                if (!accounts) return;
                accounts.forEach(a => {
                    if (!a._uid) this.$set(a, '_uid', a.account_id ? `id-${a.account_id}` : nextUid());
                    if (a.employee && !this.employeeOptions[a._uid]) {
                        this.$set(this.employeeOptions, a._uid, [a.employee]);
                    }
                });
            }
        }
    },
    computed: {
        model() {
            return this.value;
        },
        filteredAccounts() {
            const list = this.model.accounts || [];
            const q = (this.accountSearch || "").trim().toLowerCase();
            if (!q) return list;
            return list.filter(a => {
                const empName = a.employee ? (a.employee.employee_name_en || a.employee.employee_name_kh || "") : "";
                return (
                    (a.account_no || "").toLowerCase().includes(q) ||
                    (a.account_name || "").toLowerCase().includes(q) ||
                    empName.toLowerCase().includes(q)
                );
            });
        },
        readonlyColumns() {
            return [
                {
                    title: this.$t("bankProfile.employeeId"),
                    key: "employee_id",
                    width: 200,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.accountNo"),
                    key: "account_no",
                    minWidth: 160
                },
                {
                    title: this.$t("bankProfile.accountName"),
                    key: "account_name",
                    minWidth: 200
                },
                {
                    title: this.$t("bankProfile.qrImage"),
                    slot: "qr_readonly",
                    width: 70,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.isReceiveCashTransfer"),
                    key: "is_receive_cash_transfer",
                    width: 140,
                    align: "center",
                    render: (h, { row }) => h("i-switch", { props: { value: row.is_receive_cash_transfer, size: "small", disabled: true } })
                }
            ];
        },
        editableColumns() {
            return [
                {
                    title: this.$t("bankProfile.employeeId"),
                    slot: "employee_id",
                    width: 200
                },
                {
                    title: this.$t("bankProfile.accountNo"),
                    slot: "account_no",
                    minWidth: 160
                },
                {
                    title: this.$t("bankProfile.accountName"),
                    slot: "account_name",
                    minWidth: 200
                },
                {
                    title: this.$t("bankProfile.qrImage"),
                    slot: "qr_image",
                    width: 110,
                    align: "center"
                },
                {
                    title: this.$t("bankProfile.isReceiveCashTransfer"),
                    slot: "is_receive_cash_transfer",
                    width: 140,
                    align: "center"
                },
                {
                    title: "",
                    slot: "action",
                    align: "center",
                    width: 60
                }
            ];
        }
    },
    methods: {
        originalIndex(row) {
            return this.model.accounts.indexOf(row);
        },
        addRow() {
            this.model.accounts.unshift({
                _uid: nextUid(),
                employee_id: null,
                account_no: "",
                account_name: "",
                qr_image: null,
                is_receive_cash_transfer: false
            });
            this.accountSearch = "";
        },
        removeRow(row) {
            const idx = this.model.accounts.indexOf(row);
            if (idx < 0) return;

            const uid = row._uid;

            if (row.account_id) {
                this.$set(this.deletingAccount, uid, true);
                bankAccount.destroy(row.account_id)
                    .then(() => {
                        this.model.accounts.splice(idx, 1);
                    })
                    .catch(err => {
                        this.$Notice.error({
                            title: 'BANK PROFILE',
                            desc: (err && err.message) || 'Failed to delete account.'
                        });
                    })
                    .finally(() => {
                        this.$delete(this.deletingAccount, uid);
                    });
            } else {
                this.model.accounts.splice(idx, 1);
            }
        },
        onQrChange(event, row) {
            const file = event.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append("photo", file);
            this.$set(this.uploadingQr, row._uid, true);
            this.$store
                .dispatch("finance/bankProfile/qrUpload", formData)
                .then(path => { row.qr_image = `/api/files?filePath=${path}`; })
                .finally(() => { this.$set(this.uploadingQr, row._uid, false); });
            event.target.value = "";
        },
        searchEmployee: debounce(function(query, uid) {
            this.$set(this.loadingEmployee, uid, true);
            this.$store
                .dispatch("finance/bankProfile/searchEmployees", query)
                .then(data => { this.$set(this.employeeOptions, uid, data); })
                .finally(() => { this.$set(this.loadingEmployee, uid, false); });
        }, 400)
    }
};
</script>
